import { IReactionDisposer, reaction } from 'mobx';
import { KeyedComputedCache } from '@weavix/mobx';
import { featureFlagIsEnabled } from '@weavix/domain/src/feature-flag/flag-is-enabled';
import { ProductPackageEntitlements, sameEntitlements } from '@weavix/domain/src/packaging/resolve-product-packages';
import {
    resolveSyncedSiteEntitlements, SyncedEntitlementFallbackReport, SyncedEntitlementFallbackReporter, syncedEntitlementInputs,
} from '@weavix/domain/src/packaging/resolve-synced-site-entitlements';
import { FeatureId } from '@weavix/models/src/features/feature';
import { FEATURE_CATALOG } from '@weavix/models/src/packaging/feature-catalog';
import { facilitiesContext } from 'models-mobx/facilities-store/facilities-store';
import { FeatureFlag, featureFlagsContext } from 'models-mobx/feature-flags-store/feature-flags-store';
import { featuresContext } from 'models-mobx/feature-flags-store/features-store';
import { productPackagesContext } from './product-packages-store';

let fallbackReporter: ((report: SyncedEntitlementFallbackReport) => void) | undefined;
const cache = new KeyedComputedCache<ProductPackageEntitlements | null>(sameEntitlements);
const fallbacks = new SyncedEntitlementFallbackReporter(report => fallbackReporter?.(report));

/** Sets where a fallback is emitted as `ProductPackageEntitlementFallback`; the Angular telemetry service registers itself. */
export function setSiteEntitlementsFallbackReporter(reporter: (report: SyncedEntitlementFallbackReport) => void): void {
    fallbackReporter = reporter;
}

/**
 * What a site of `accountId` is entitled to from its synced packages, or the whole account's packages without a site.
 * Null while `SiteScopedPackages` is off or nothing usable has synced; callers then use `enabledActions`. The result keeps
 * its identity until what it licenses changes, so permission checks can memoize on it.
 */
export function siteEntitlements(accountId: string, facilityId?: string): ProductPackageEntitlements | null {
    if (!storesExist()) return null;
    return cache.get(`${accountId}:${facilityId ?? ''}`, () => resolveSiteEntitlements(accountId, facilityId));
}

/** Drops every cached resolution, e.g. on logout; one entry exists per account and site checked. */
export function clearSiteEntitlements(): void {
    cache.clear();
    fallbacks.clear();
}

/** Calls `listener` whenever synced packages, site packages, or the `SiteScopedPackages` flag or feature change; returns its disposer. */
export function watchSiteEntitlements(listener: () => void): IReactionDisposer {
    return reaction(() => syncedEntitlementInputs(
        productPackagesContext.get()?.getAll() ?? [],
        facilitiesContext.get()?.getAllSynced() ?? [],
        featureFlagsContext.get()?.getAll((flag: FeatureFlag) => flag.featureId === FeatureId.SiteScopedPackages) ?? [],
        featuresContext.get()?.get(FeatureId.SiteScopedPackages),
    ), listener);
}

/** Resolves a site from the synced stores, reporting a site whose packages are all undefined. */
function resolveSiteEntitlements(accountId: string, facilityId?: string): ProductPackageEntitlements | null {
    if (!isSiteScopedPackagesOn(accountId)) return null;
    const records = productPackagesContext.get()?.getAll() ?? [];
    const sites = sitesToResolve(accountId, facilityId);
    const result = resolveSyncedSiteEntitlements(accountId, facilityId ?? null, records, sites, FEATURE_CATALOG);
    fallbacks.report(accountId, facilityId, result);
    return result.entitlements;
}

/** Whether the account-level `SiteScopedPackages` flag is on; off until the feature and its flags load. */
function isSiteScopedPackagesOn(accountId: string): boolean {
    const feature = featuresContext.get()?.get(FeatureId.SiteScopedPackages);
    if (!feature) return false;
    const flags = featureFlagsContext.get()?.getAll((x: FeatureFlag) =>
        x.featureId === FeatureId.SiteScopedPackages && x.accountId === accountId && x.enabled !== undefined) ?? [];
    return featureFlagIsEnabled(FeatureId.SiteScopedPackages, feature, flags, [], false).enabled;
}

/** The one site checked, or every site of the account for an account-wide check, so a site check observes only its own record. */
function sitesToResolve(accountId: string, facilityId?: string) {
    const facilities = facilitiesContext.get();
    if (!facilityId) return facilities?.getAccountFacilities(accountId) ?? [];
    const site = facilities?.get(facilityId);
    return site ? [site] : [];
}

/**
 * Whether sign-in has created the stores entitlements are resolved from. Until then a check falls back without caching,
 * since their contexts are not observable and a cached answer would never see them arrive.
 */
function storesExist(): boolean {
    return !!(productPackagesContext.get() && facilitiesContext.get() && featureFlagsContext.get() && featuresContext.get());
}
